/**
 * Analysis lifecycle (`app/services/analysis_service.py`).
 *
 * Split out of `brainfocus_api.ts` (FE Arch 1.0, #1165), which re-exports everything
 * here through the `BrainFocusApi` facade. Requests go through `client.request` so tests
 * can stub it on the module namespace.
 */
import * as client from '@brainfocus/web/api/client';
import { assert } from '@brainfocus/web/utils/assert';
import { EmptyObject } from '@brainfocus/web/utils/types';
import { NormType } from '@brainfocus/web/analysis/models';
import { ChannelKind } from '@brainfocus/web/utils/channel_kind';
import type { SerializedLayout } from '@brainfocus/web/utils/channel_layout_serialization';
import type { UserAnnotation } from '@brainfocus/web/domain/annotations/user_annotation';
import { Montage } from '@brainfocus/web/domain/montage/montage';
import { assertPojo } from '@brainfocus/web/api/response';
import type { ElectrodeColormapRanges, Analysis, RawEdfAnnotation } from '@brainfocus/web/api/types';

export async function runAnalysis(payload: RunAnalysisPayload): Promise<{ analysisIds: number[] }> {
    const response = await client.request('POST', '/api/run-analysis', payload);
    assertPojo<{ analysisIds: number[] }>(response);
    return response;
}

export async function createPendingAnalysis(edfId: number, patientId: number): Promise<{ analysisId: number }> {
    const response = await client.request('POST', '/api/create-pending-analysis', {
        edf_id: edfId,
        patient_id: patientId,
    });
    assertPojo<{ analysisId: number }>(response);
    return response;
}

/**
 * i718: `escalatedToFullReanalysis` is true when the backend silently
 * escalated this recompute to a full re-analysis (analysis_service.py's
 * `recompute_analysis()`) because the recording was blocked by a
 * too-short baseline before the tiered fallback existed -- a plain
 * recompute only re-scores AD against the stored baseline and cannot fix
 * that on its own.
 */
export async function recompute(id: number): Promise<{ escalatedToFullReanalysis: boolean }> {
    const response = await client.request('POST', `/api/recompute`, {
        recording_id: id,
    });
    assertPojo<{ escalatedToFullReanalysis: boolean }>(response);
    return response;
}

/**
 * Cooperatively cancel a running analysis or recompute task (#430).
 * Returns ``{aborted: true}`` once the worker has been signalled.
 */
export async function abortAnalysis(id: number): Promise<{ aborted: boolean }> {
    const response = await client.request('POST', `/api/abort-analysis/${id}`, {});
    assertPojo<{ aborted: boolean }>(response);
    return response;
}

/**
 * Re-run a failed analysis from scratch with the original parameters (#722).
 * Returns ``{analysisIds: [...]}`` once the fresh task has been dispatched.
 */
export async function retryAnalysis(id: number): Promise<{ analysisIds: number[] }> {
    const response = await client.request('POST', `/api/retry-analysis/${id}`, {});
    assertPojo<{ analysisIds: number[] }>(response);
    return response;
}

/**
 * Opt-in migration of a legacy-methodology analysis to the moving-window
 * detection methodology, with fixed new-methodology defaults (#922).
 * Returns ``{analysisIds: [...]}`` once the fresh task has been dispatched.
 */
export async function recomputeToMovingZscore(id: number): Promise<{ analysisIds: number[] }> {
    const response = await client.request('POST', `/api/recompute-to-moving-zscore/${id}`, {});
    assertPojo<{ analysisIds: number[] }>(response);
    return response;
}

export async function updateAnalysis(id: number, info: UpdatableAnalysisInfo): Promise<EmptyObject> {
    await client.request('POST', `/api/update-analysis`, {
        analysisId: id,
        badChannels: info.badChannels,
        cleanChannels: info.cleanChannels,
        candidateChannels: info.candidateChannels,
        earlyPropagationChannels: info.earlyPropagationChannels,
        latePropagationChannels: info.latePropagationChannels,
        onset: info.onset,
        duration: info.duration,
        thresholdAe: info.aeThreshold,
        thresholdGa: info.gaThreshold,
        zscoreThreshold: info.zscoreThreshold,
        isFocal: info.isFocal,
        minDurationEnforced: info.minDurationEnforced,
        preOnsetDetection: info.preOnsetDetection,
        notes: info.notes,
        seizureGroupName: info.seizureGroupName,
        showAlgorithmOutput: info.showAlgorithmOutput,
        showEdfAnnotations: info.showEdfAnnotations,
        metricsEegOverlay: info.metricsEegOverlay,
        metricsEegOverlayGain: info.metricsEegOverlayGain,
        metricsShading: info.metricsShading,
        metricsShadingThreshold: info.metricsShadingThreshold,
        normType: info.normType,
        electrodeColormapRanges: info.electrodeColormapRanges,
    });
    return {};
}

export async function deleteAnalysis(id: number): Promise<EmptyObject> {
    await client.request('POST', `/api/delete-analysis/${id}`, {});
    return {};
}

/**
 * Load MA matrix NPY and return it as Blob so it can easily downloaded
 */
export async function downloadNpy(recId: number): Promise<Blob> {
    const r = await client.request('POST', `/admin/download-ma`, { analysis_id: recId });
    assert(r instanceof ArrayBuffer, 'Get TC endpoint returned unexpected data');
    return new Blob([r]);
}

export async function getAnalysis(analysisId: number): Promise<AnalysisDetails> {
    const response = await client.request('POST', '/api/get-analysis', {
        analysisId,
    });
    assertPojo<AnalysisDetails>(response);
    return response;
}

/**
 * Get list of accessible analyses based on input filters
 */
export async function getAnalyses(payload: GetAnalysesPayload): Promise<Analysis[]> {
    const response = await client.request('POST', '/api/get-analyses', {
        patient_ids: payload.patientIds,
        uploader_ids: payload.uploaderIds,
        count: payload.count,
        institution_id: payload.institutionId,
        demo: payload.demo,
    });

    assertPojo<{ analyses: Analysis[] }>(response);
    return response.analyses;
}

/**
 * Get the current trial user's ephemeral analysis quota usage (#584).
 */
export async function getEphemeralQuota(): Promise<EphemeralQuota> {
    const response = await client.request('GET', '/api/ephemeral-quota');

    assertPojo<EphemeralQuota>(response);
    return response;
}

export interface GetAnalysesPayload {
    patientIds: number[];
    uploaderIds: number[];
    count: number;
    institutionId?: number | undefined;
    demo?: boolean;
}

/** A trial user's ephemeral analysis quota usage (#584). */
export interface EphemeralQuota {
    /** Number of ephemeral analyses the user currently holds. */
    used: number;
    /** Configured maximum simultaneous ephemeral analyses. */
    limit: number;
}

/**
 * @todo Merge with the other analysis detail / info interfaces
 */
export interface AnalysisDetails {
    analysis: Analysis;
    edf: EdfMeta;
    /**
     * Only available for finished analyses
     */
    calcMeta?: CalcMeta;
    /**
     * User-authored time annotations on this recording (issue #365). Always
     * present; empty array when the recording has none.
     */
    annotations: UserAnnotation[];
}

export interface RunAnalysisPayload {
    edf_id: number;
    patient_id: number;
    recordings: RunAnalysisRecording[];
    channels: string[];
    montage: Montage | null;
    group: string | null;
    /**
     * sEEG Preview layout the dialog was pre-configured with (#852), stored on
     * the created analysis as its initial layout. Omitted from other entry points.
     */
    channel_layout?: SerializedLayout;
    /**
     * FAILED analysis to re-run in place with this setup (#748). The first
     * seizure of the request reuses that recording; further ones create new.
     */
    retry_of?: number;
}

export interface RunAnalysisRecording {
    label: string;
    onset: number;
    duration: number;
}

/**
 * @todo Merge together with EdfInfo and RawEdfInfo since this is a major mess
 * now
 */
export interface EdfMeta {
    startdate: string;
    /**
     * Recording start as epoch ms with the wall-clock encoded in UTC
     * components, for the video-sEEG clock (#565). Independent of server/browser
     * timezone; format with the UTC getters (see `formatAbsoluteClock`). Null
     * when the recording has no start datetime.
     */
    recStartWallClockMs: number | null;
    length: number;
    badChannels: number[];
    /**
     * List of all channel names available in the EDF. Unlike
     * {@link CalcMeta#channellabels} this includes also channels not included
     * the analysis. This information is only needed during the analysis
     * creation. During the analysis visualization, all channel indexes are
     * relative to the {@link CalcMeta#channellabels}.
     */
    channelNames: string[];
    /**
     * `seeg` / `other` for each of {@link EdfMeta#channelNames}, from the file's own signal
     * header (label heuristic only for legacy uploads). Optional: payloads cached before it
     * existed do not carry it, and callers then fall back to the label alone (PR #1012).
     */
    channelKinds?: ChannelKind[];
    annotations: RawEdfAnnotation[];
}

/**
 * Recording / seizure metadata created by the calculation function and served
 * by the API from storage.
 */
export interface CalcMeta {
    /**
     * List of channels on which the analysis was actually run.
     */
    channellabels: string[];
    /**
     * Bipolar analyses only (#908): the `[first, second]` contacts behind each
     * row, aligned with `channellabels`. Absent/null for other montages and for
     * analyses computed before the field existed.
     */
    bipolar_members?: [string, string][] | null;
    d1: number;
    d2: number;
    /**
     * Canonical band edges in Hz, length Nbands + 1 (e.g. [0.5, 4, 8, 12, 30, 50]).
     */
    freqs: number[];
    /**
     * Canonical band names aligned with the bands defined by {@link freqs}
     * (e.g. ["delta", "theta", "alpha", "beta", "gamma"]). Absent on legacy
     * analyses computed before canonical bands were introduced (#510).
     */
    band_names?: string[];
    /**
     * Indices of frequency bands skipped during preprocessing (exceed Nyquist).
     */
    skipped_bands: number[];
    /**
     * Sampling frequency of the data
     */
    fs: number;
    /**
     * Number of samples in each channel, named Nt in the original metadata
     */
    samples: number;
    /**
     * @deprecated Use {@link Analysis#seizureDuration} instead
     */
    szduration: number;
    /**
     * @deprecated Use {@link Analysis#seizureOnset} instead
     */
    szstart: number;
    /**
     * Offset (in original-EDF seconds) at which the cropped recording starts.
     * Legacy analyses without this field default to 0.
     */
    crop_tmin_s?: number;
    /**
     * Stationarity-driven baseline selected for SAM normalization (#582). The
     * SAM plot normalizes against the per-band baseline TC block (fetched via
     * {@link BrainFocusApi#getBaseline}) rather than slicing the analysis
     * series. ``null``/absent means no acceptable baseline window was found, in
     * which case the SAM plot shows a "no baseline available" placeholder.
     */
    baseline?: BaselineInfo | null;
    /**
     * Reason code explaining why no baseline window qualified, set only when
     * {@link CalcMeta#baseline} is `null` (#582 review round 5). One of
     * `no_good_channels` | `no_seizure_onset` | `recording_too_short` |
     * `sampling_rate_too_low`. Drives an actionable "no baseline available"
     * message instead of one catch-all string. Absent on legacy analyses.
     */
    baseline_reason?: string | null;
    /**
     * Tiered baseline selection (#718): which tier produced {@link CalcMeta#baseline},
     * always set alongside it (even when `baseline` is `null`, in which case this
     * is `"none"`). One of `"full"` | `"reduced"` | `"minimal"` | `"none"`.
     * Absent on legacy analyses computed before #718.
     */
    baseline_tier?: BaselineTier;
}

/**
 * Tiered baseline selection (#718). `"full"` (>= 210s pre-ictal, unchanged
 * behavior) and `"reduced"` (30-210s, same CV-based search with a shrunk gap)
 * both carry a full {@link BaselineInfo} including `score`/`per_band_cv`.
 * `"minimal"` (5-30s, entire available window, no search) omits those two
 * fields (`null`). `"none"` (<5s) means no baseline was selected at all.
 */
export type BaselineTier = 'full' | 'reduced' | 'minimal' | 'none';

/**
 * Stationarity-driven baseline metadata (#582). All sample indices are in
 * original-EDF (pre-crop) coordinates; they are for display/provenance only —
 * normalization consumes the baseline TC block, not these indices.
 */
export interface BaselineInfo {
    d1: number;
    d2: number;
    /**
     * Composite per-band coefficient of variation (lower = more stationary).
     * `null` for the "minimal" tier (#718), which performs no CV search.
     */
    score: number | null;
    /** Per-band CV keyed by "fl-fhHz". `null` for the "minimal" tier (#718). */
    per_band_cv: Record<string, number> | null;
    start_s: number;
    end_s: number;
    time_to_sz_s: number;
    n_candidates: number;
    n_valid: number;
    /** True when no artifact-free / stationary window qualified. */
    fallback: boolean;
    /** Tiered baseline selection (#718); see {@link BaselineTier}. */
    tier: BaselineTier;
}

export interface UpdatableAnalysisInfo {
    onset?: number;
    duration?: number;
    badChannels?: number[];
    cleanChannels?: number[];
    candidateChannels?: number[];
    earlyPropagationChannels?: number[];
    latePropagationChannels?: number[];
    aeThreshold?: number;
    gaThreshold?: number;
    /**
     * GA z-score threshold for the moving-window methodology (#922).
     */
    zscoreThreshold?: number;
    /**
     * Channel-activation scope for the moving-window methodology (#922):
     * `true` = "Narrow channel activation", `false` = "Broad channel
     * activation" (field name `isFocal`).
     */
    isFocal?: boolean;
    /**
     * Minimum flagged-run duration gate for the moving-window methodology
     * (#943). See {@link Analysis#minDurationEnforced}.
     */
    minDurationEnforced?: boolean;
    /**
     * Pre-onset detection extension (#943). See {@link Analysis#preOnsetDetection}.
     */
    preOnsetDetection?: boolean;
    notes?: string;
    seizureGroupName?: string | null;
    showAlgorithmOutput?: boolean;
    showEdfAnnotations?: boolean;
    metricsEegOverlay?: boolean;
    metricsEegOverlayGain?: number | null;
    metricsShading?: boolean;
    metricsShadingThreshold?: number | null;
    /**
     * SAM / automatic-detection normalization mode (#733). Persisting a new
     * value marks the analysis diverged with reason `'norm_type'`.
     */
    normType?: NormType;
    /**
     * Per-electrode colormap range overrides for the METRICS/Explore TC plot
     * (#922 follow-up). A pure display preference -- never marks the
     * analysis diverged. An empty object clears every override.
     */
    electrodeColormapRanges?: ElectrodeColormapRanges;
}
